import { AssortmentTemplate, AssortmentType } from "containers/folders/types/base";
import { createContext } from "hooks/create-context";
import * as React from "react";

export type AssortmentContextType = {
    isEditing: boolean;
    formValues: FormValues;
};

export type selectedDimensionValues = Record<string, string[]>;

export type FormValues = {
    assortmentTemplate?: AssortmentTemplate;
    brandId?: string;
    dimensionsValues: selectedDimensionValues;
    assortmentType: AssortmentType;
    assortmentName?: string;
};

export type DropdownOption = {
    id: string;
    key: string;
    text: string;
    disabled?: boolean;
};

export enum ActionsTypes {
    UPDATE_ASSORTMENT_TYPE = "UPDATE_ASSORTMENT_TYPE",
    UPDATE_ASSORTMENT_TEMPLATE = "UPDATE_ASSORTMENT_TEMPLATE",
    UPDATE_BRAND_ID = "UPDATE_BRAND_ID",
    UPDATE_SELECTED_DIMENSIONS_VALUES = "UPDATE_SELECTED_DIMENSIONS_VALUES",
    UPDATE_FORM_VALUES = "UPDATE_FORM_VALUES",
    UPDATE_ASSORTMENT_NAME = "UPDATE_ASSORTMENT_NAME",
}

export type UpdateFormValuesAction = {
    type: ActionsTypes.UPDATE_FORM_VALUES;
    payload: FormValues;
};

export type UpdateAssortmentTemplateAction = {
    type: ActionsTypes.UPDATE_ASSORTMENT_TEMPLATE;
    payload: AssortmentTemplate;
};

export type UpdateAssortmentTypeAction = {
    type: ActionsTypes.UPDATE_ASSORTMENT_TYPE;
    payload: AssortmentType;
};

export type UpdateBrandIdAction = {
    type: ActionsTypes.UPDATE_BRAND_ID;
    payload: string;
};

export type UpdateSelectedDimensionsValues = {
    type: ActionsTypes.UPDATE_SELECTED_DIMENSIONS_VALUES;
    payload: {
        id: string;
        values: string[];
    };
};

export type UpdateAssortmentNameAction = {
    type: ActionsTypes.UPDATE_ASSORTMENT_NAME;
    payload: string;
};

type Actions =
    | UpdateAssortmentTemplateAction
    | UpdateAssortmentTypeAction
    | UpdateBrandIdAction
    | UpdateSelectedDimensionsValues
    | UpdateFormValuesAction
    | UpdateAssortmentNameAction;

type DispatchType = { dispatch: React.Dispatch<Actions> };

export const reducer = (state: AssortmentContextType, action: Actions): AssortmentContextType => {
    switch (action.type) {
        case ActionsTypes.UPDATE_FORM_VALUES:
            return {
                ...state,
                formValues: action.payload,
            };
        case ActionsTypes.UPDATE_SELECTED_DIMENSIONS_VALUES:
            const { id, values } = action.payload;
            return {
                ...state,
                formValues: {
                    ...state.formValues,
                    dimensionsValues: {
                        ...state.formValues.dimensionsValues,
                        [id]: values,
                    },
                },
            };
        case ActionsTypes.UPDATE_BRAND_ID:
            if (state.isEditing) {
                return state;
            }
            if (state.formValues.assortmentType === AssortmentType.Internal) {
                return state;
            }
            return {
                ...state,
                formValues: {
                    ...state.formValues,
                    brandId: action.payload,
                },
            };
        case ActionsTypes.UPDATE_ASSORTMENT_TEMPLATE:
            if (state.formValues.assortmentType === AssortmentType.Internal) {
                return state;
            }

            if (state.isEditing) {
                return state;
            }

            return {
                ...state,
                formValues: {
                    ...state.formValues,
                    assortmentTemplate: action.payload,
                },
            };
        case ActionsTypes.UPDATE_ASSORTMENT_TYPE:
            if (state.isEditing) {
                return state;
            }

            return {
                ...state,
                formValues: {
                    ...state.formValues,
                    assortmentType: action.payload,
                    brandId: action.payload === AssortmentType.Internal ? undefined : state.formValues.brandId,
                },
            };
        case ActionsTypes.UPDATE_ASSORTMENT_NAME:
            if (state.isEditing) {
                return state;
            }

            return {
                ...state,
                formValues: {
                    ...state.formValues,
                    assortmentName: action.payload,
                },
            };
        default:
            return state;
    }
};

export const useAssortmentDialogReducer = (initialState: AssortmentContextType) =>
    React.useReducer(reducer, initialState);

type AssortmentDialogContextType = AssortmentContextType & DispatchType;

export const [useAssortmentDialogContext, AssortmentDialogContextProvider] =
    createContext<AssortmentDialogContextType>();
